import React, { useState } from 'react';
import {
  Calendar,
  DollarSign,
  Star,
  TrendingUp,
  BarChart3,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  ListChecks,
} from 'lucide-react';
import { useVendorAnalytics } from '../../services/vendorAnalyticsService';
import FormattedDate from '../../components/shared/FormattedDate';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const PERIOD_TABS = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
];

const VendorAnalytics = () => {
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useVendorAnalytics(period);

  const analytics = data?.data || {};

  const formatValue = (value, formatter) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    return formatter(value);
  };

  const keyMetrics = [
    {
      label: 'Total Bookings',
      value: analytics.total_bookings || 0,
      icon: Calendar,
      color: 'text-sky-600 dark:text-sky-400',
      bgColor: 'bg-sky-50 dark:bg-sky-950/40 border-sky-100 dark:border-sky-800/50',
    },
    {
      label: 'Total Revenue',
      value: formatValue(analytics.total_revenue, (val) =>
        `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      ),
      icon: DollarSign,
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-800/50',
    },
    {
      label: 'Average Rating',
      value: formatValue(analytics.average_rating, (val) => `${Number(val).toFixed(1)}/5`),
      icon: Star,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-950/40 border-amber-100 dark:border-amber-800/50',
    },
    {
      label: 'Total Reviews',
      value: analytics.total_reviews || 0,
      icon: BarChart3,
      color: 'text-primary-600 dark:text-primary-400',
      bgColor: 'bg-primary-50 dark:bg-primary-950/40 border-primary-100 dark:border-primary-800/50',
    },
  ];

  const secondaryMetrics = [
    {
      label: 'Booking Conversion Rate',
      value: formatValue(analytics.booking_conversion_rate, (val) => `${Number(val).toFixed(1)}%`),
      description: 'Percentage of inquiries converted to bookings',
    },
    {
      label: 'Average Booking Value',
      value: formatValue(analytics.average_booking_value, (val) =>
        `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      ),
      description: 'Average revenue per completed booking',
    },
  ];

  const cardClass =
    'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6';

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Analytics"
        subtitle="Charts and metrics fill in as you get bookings and profile views — change the period to compare."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.analytics.title}
        steps={VENDOR_PAGE_GUIDES.analytics.steps}
        icon={ListChecks}
      />

      <div
        className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
        role="tablist"
        aria-label="Analytics period"
      >
        {PERIOD_TABS.map((tab) => {
          const isActive = period === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setPeriod(tab.value)}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                isActive
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`${cardClass} animate-pulse`}>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-4" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-300 px-4 py-3 text-sm">
          Failed to load analytics data. Please try again.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {keyMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className={cardClass}>
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`w-12 h-12 border ${metric.bgColor} rounded-xl flex items-center justify-center`}
                    >
                      <Icon className={`w-6 h-6 ${metric.color}`} />
                    </div>
                    {metric.change !== null &&
                      metric.change !== undefined &&
                      !isNaN(metric.change) && (
                        <div
                          className={`flex items-center gap-1 text-sm ${
                            metric.change >= 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {metric.change >= 0 ? (
                            <ArrowUpRight className="w-4 h-4" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4" />
                          )}
                          <span>{Math.abs(metric.change).toFixed(1)}%</span>
                        </div>
                      )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{metric.label}</p>
                  <p className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50 tabular-nums">
                    {metric.value}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {secondaryMetrics.map((metric, index) => (
              <div key={index} className={cardClass}>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{metric.label}</p>
                <p className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50 mb-2 tabular-nums">
                  {metric.value}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{metric.description}</p>
              </div>
            ))}
          </div>

          {analytics.top_services && analytics.top_services.length > 0 && (
            <div className={cardClass}>
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                  Top Performing Services
                </h2>
              </div>
              <div className="space-y-3">
                {analytics.top_services.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-50 truncate">
                        {service.service_name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {service.booking_count || 0} bookings
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-50 tabular-nums">
                        $
                        {Number(service.total_revenue || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">revenue</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {analytics.booking_trends && analytics.booking_trends.length > 0 && (
            <div className={`${cardClass} overflow-hidden`}>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Booking Trends</h2>
              </div>
              <div className="overflow-x-auto -mx-1">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                        Period
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                        Bookings
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                        Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.booking_trends.map((trend, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/80 dark:hover:bg-gray-800/40"
                      >
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100">
                          <FormattedDate date={trend.period} format="auto" />
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right tabular-nums">
                          {trend.booking_count || 0}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-50 text-right tabular-nums">
                          $
                          {Number(trend.revenue || 0).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!analytics.top_services || analytics.top_services.length === 0) &&
            (!analytics.booking_trends || analytics.booking_trends.length === 0) && (
              <div className={`${cardClass} p-8 sm:p-12 text-center`}>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                  <BarChart3 className="w-7 h-7" />
                </div>
                <p className="text-gray-600 dark:text-gray-300">No analytics data available for this period</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Start getting bookings to see your analytics
                </p>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default VendorAnalytics;
